/**
 * Display formatters and English labels for a movement result, keyed on its
 * `measureType` (the orthogonal unit: kg / reps / time / calories / distance /
 * binary), NOT its `scoringType` (the scoring shape: higherIsBetter /
 * lowerIsBetter / binary). The unit lives on `measureType` — format on
 * `measureType`, score-direction on `scoringType`.
 *
 * Formatters are re-exported from `@lemonadetraining/contracts` so mobile, web,
 * and Cloud Functions all read off one canonical contract — keeps display
 * output (zero/negative duration sentinel, kg/reps rounding, thousands
 * grouping) identical across platforms.
 *
 * `MEASURE_TYPE_LABELS` is the single source of English form/heading labels
 * for each unit. Both athlete-facing surfaces (e.g. log dialogs) and
 * admin-facing surfaces (e.g. registry editor) import it so a copy edit lands
 * everywhere at once.
 */
import {
  formatMeasureValue,
  MOVEMENT_MEASURE_TYPE,
  MOVEMENT_RECORD_KIND,
  type MovementMeasureType,
  type MovementRecordKind,
} from "@lemonadetraining/contracts";

// `measureUnitSuffix` is also available from @lemonadetraining/contracts but is
// not re-exported here — no app/ src caller uses it. Import it directly from
// the contracts package if a future need arises.
export { formatDuration, formatMeasureValue } from "@lemonadetraining/contracts";

/**
 * English labels per measure unit. `BINARY` reads as "Unlock" — used by
 * admin surfaces (the athlete log dialog renders binary as a button pair, no
 * label).
 */
export const MEASURE_TYPE_LABELS: Record<MovementMeasureType, string> = {
  [MOVEMENT_MEASURE_TYPE.KG]: "Weight (kg)",
  [MOVEMENT_MEASURE_TYPE.REPS]: "Reps",
  [MOVEMENT_MEASURE_TYPE.TIME]: "Time (seconds)",
  [MOVEMENT_MEASURE_TYPE.CALORIES]: "Calories",
  [MOVEMENT_MEASURE_TYPE.DISTANCE]: "Distance (metres)",
  [MOVEMENT_MEASURE_TYPE.BINARY]: "Unlock",
};

/** The headline over a movement's current result, by unit (movement page, Records). */
export const STAT_HEADING_BY_MEASURE: Record<MovementMeasureType, string> = {
  [MOVEMENT_MEASURE_TYPE.KG]: "Current 1RM",
  [MOVEMENT_MEASURE_TYPE.REPS]: "Best result",
  [MOVEMENT_MEASURE_TYPE.TIME]: "Best time",
  [MOVEMENT_MEASURE_TYPE.CALORIES]: "Best result",
  [MOVEMENT_MEASURE_TYPE.DISTANCE]: "Best result",
  [MOVEMENT_MEASURE_TYPE.BINARY]: "Status",
};

/** A working set is logged only for kg and reps; its value is an estimate, never the current max. */
export function statHeading(
  measure: MovementMeasureType,
  kind?: MovementRecordKind | null
): string {
  if (kind === MOVEMENT_RECORD_KIND.WORKING_SET) {
    if (measure === MOVEMENT_MEASURE_TYPE.KG) return "Estimated 1RM";
    if (measure === MOVEMENT_MEASURE_TYPE.REPS) return "Estimated result";
  }
  return STAT_HEADING_BY_MEASURE[measure];
}

/** Labels for a non-kg movement's latest value. */
export const CURRENT_TIME_LABEL = "Current time";
export const CURRENT_RESULT_LABEL = "Current result";

/**
 * The label over a movement's latest value (movement page and Records agree):
 * never "Best", which it may not be; a working-set estimate says it is one.
 */
export function currentValueLabel(
  measure: MovementMeasureType,
  kind?: MovementRecordKind | null
): string {
  if (
    measure === MOVEMENT_MEASURE_TYPE.KG ||
    measure === MOVEMENT_MEASURE_TYPE.BINARY ||
    kind === MOVEMENT_RECORD_KIND.WORKING_SET
  ) {
    return statHeading(measure, kind);
  }
  return measure === MOVEMENT_MEASURE_TYPE.TIME ? CURRENT_TIME_LABEL : CURRENT_RESULT_LABEL;
}

/** An estimate (e1RM) as shown: at most one decimal, never a trailing ".0". Stored values keep theirs. */
export function formatEstimate(value: number, measureType: MovementMeasureType): string {
  if (measureType !== MOVEMENT_MEASURE_TYPE.KG) return formatMeasureValue(value, measureType);
  return `${Math.round(value * 10) / 10} kg`;
}
